import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faPalette,
  faBell,
  faShieldAlt,
  faDatabase,
  faGlobe,
  faSave,
  faUndo,
  faMoon,
  faSun,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faLock,
  faEnvelope,
  faServer,
  faUserLock,
  faClock,
  faUserShield,
  faQrcode,
  faKey,
  faSpinner,
  faCopy,
  faTerminal,
  faTrash,
  faSync,
  faSearch,
  faFilter,
  faBug,
  faEye,
  faBullhorn,
  faTimes,
  faPlus,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import api from '../../services/api';
import RepairCodePanel from './RepairCodePanel';
import ResetCodePanel from './ResetCodePanel';
import './Settings.css';

const Settings = () => {
  const { admin, hasPermission } = useDashboardAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    // General
    site_name: 'Lonnii Dashboard',
    site_url: 'https://lonnii.com',
    maintenance_mode: false,
    
    // Appearance
    default_theme: 'light',
    primary_color: '#3b82f6',
    
    // Notifications
    email_notifications: true,
    login_alerts: true,
    activity_digest: 'daily',
    notify_on_new_user: true,
    notify_on_new_group: false,
    
    // Security
    session_timeout: 60,
    require_strong_passwords: true,
    two_factor_required: false,
    login_attempt_limit: 5,
    lockout_duration: 15,
    
    // System
    auto_backup: true,
    backup_frequency: 'daily',
    log_retention: 90,
    debug_mode: false,

    // Banner
  });

  // Multi-banner state
  const [banners, setBanners] = useState([]);
  const [expandedBannerId, setExpandedBannerId] = useState(null);

  const [savedSettings, setSavedSettings] = useState({ ...settings });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // 2FA state
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, verifiedAt: null });
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [setting2FA, setSetting2FA] = useState(false);

  // Banner state
  const [bannerGroups, setBannerGroups] = useState([]);
  const [bannerGroupsLoading, setBannerGroupsLoading] = useState(false);

  // System logs state
  const [showLogViewer, setShowLogViewer] = useState(false);
  const [systemLogs, setSystemLogs] = useState([]);
  const [logCounts, setLogCounts] = useState({ debug: 0, info: 0, warn: 0, error: 0 });
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState({ level: '', source: '', search: '' });
  const [logSources, setLogSources] = useState([]);
  const [clearingLogs, setClearingLogs] = useState(false);

  // Load system logs
  const loadSystemLogs = useCallback(async () => {
    try {
      setLoadingLogs(true);
      const params = new URLSearchParams();
      if (logFilter.level) params.append('level', logFilter.level);
      if (logFilter.source) params.append('source', logFilter.source);
      if (logFilter.search) params.append('search', logFilter.search);
      
      const response = await api.get(`/dashboard-admin/system-logs?${params.toString()}`);
      if (response.data) {
        setSystemLogs(response.data.logs || []);
        setLogCounts(response.data.counts || { debug: 0, info: 0, warn: 0, error: 0 });
      }
    } catch (error) {
      console.error('Error loading system logs:', error);
      showNotification('error', 'Erreur lors du chargement des logs');
    } finally {
      setLoadingLogs(false);
    }
  }, [logFilter]);

  // Load log sources for filter
  const loadLogSources = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/system-logs/sources');
      setLogSources(response.data || []);
    } catch (error) {
      console.error('Error loading log sources:', error);
    }
  }, []);

  // Clear system logs
  const handleClearLogs = async (level = null) => {
    if (!window.confirm(level 
      ? `Voulez-vous vraiment supprimer tous les logs de niveau "${level}" ?`
      : 'Voulez-vous vraiment supprimer tous les logs système ?')) {
      return;
    }
    
    try {
      setClearingLogs(true);
      const params = level ? `?level=${level}` : '';
      await api.delete(`/dashboard-admin/system-logs${params}`);
      showNotification('success', 'Logs supprimés avec succès');
      loadSystemLogs();
    } catch (error) {
      console.error('Error clearing logs:', error);
      showNotification('error', 'Erreur lors de la suppression des logs');
    } finally {
      setClearingLogs(false);
    }
  };

  // Load settings from API
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard-admin/settings');
      if (response.data) {
        const settingsObj = {};
        response.data.forEach(s => {
          if (s.key === 'info_banner') {
            let raw = s.value;
            if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = null; } }
            if (Array.isArray(raw)) {
              setBanners(raw);
            } else if (raw && typeof raw === 'object') {
              // Migrate old single-banner format
              setBanners([{ id: Date.now().toString(), ...raw }]);
            }
          } else {
            settingsObj[s.key] = s.value;
          }
        });
        setSettings(prev => ({ ...prev, ...settingsObj }));
        setSavedSettings(prev => ({ ...prev, ...settingsObj }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      showNotification('error', 'Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load 2FA status
  const load2FAStatus = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/2fa/status');
      setTwoFactorStatus(response.data);
    } catch (error) {
      console.error('Error loading 2FA status:', error);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    load2FAStatus();
  }, [loadSettings, load2FAStatus]);

  useEffect(() => {
    // Check if settings have changed
    setHasChanges(JSON.stringify(settings) !== JSON.stringify(savedSettings));
  }, [settings, savedSettings]);

  // Load logs when log viewer is opened
  useEffect(() => {
    if (showLogViewer) {
      loadSystemLogs();
      loadLogSources();
    }
  }, [showLogViewer]);

  // Reload logs when filter changes (with debounce for search)
  useEffect(() => {
    if (showLogViewer) {
      const timeoutId = setTimeout(() => {
        loadSystemLogs();
      }, logFilter.search ? 300 : 0); // Debounce search input
      return () => clearTimeout(timeoutId);
    }
  }, [logFilter, showLogViewer]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build settings array — banners stored separately as array
      const settingsArray = [
        ...Object.entries(settings).map(([key, value]) => ({ key, value })),
        { key: 'info_banner', value: JSON.stringify(banners) },
      ];

      await api.put('/dashboard-admin/settings', { settings: settingsArray });
      setSavedSettings({ ...settings });
      
      // Apply theme if changed
      if (settings.default_theme !== savedSettings.default_theme) {
        document.documentElement.setAttribute('data-theme', settings.default_theme);
      }
      
      showNotification('success', 'Paramètres enregistrés avec succès');
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('error', 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...savedSettings });
  };

  // 2FA Setup
  const handleSetup2FA = async () => {
    try {
      setSetting2FA(true);
      const response = await api.post('/dashboard-admin/2fa/setup');
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setShowSetup2FA(true);
    } catch (error) {
      showNotification('error', 'Erreur lors de la configuration 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  // Verify 2FA
  const handleVerify2FA = async () => {
    if (verifyCode.length !== 6) {
      showNotification('error', 'Entrez un code à 6 chiffres');
      return;
    }
    try {
      setSetting2FA(true);
      const response = await api.post('/dashboard-admin/2fa/verify', { code: verifyCode });
      setBackupCodes(response.data.backupCodes);
      setTwoFactorStatus({ enabled: true, verifiedAt: new Date().toISOString() });
      showNotification('success', '2FA activé avec succès !');
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Code incorrect');
    } finally {
      setSetting2FA(false);
    }
  };

  // Disable 2FA
  const handleDisable2FA = async () => {
    if (!disablePassword) {
      showNotification('error', 'Entrez votre mot de passe');
      return;
    }
    try {
      setSetting2FA(true);
      await api.post('/dashboard-admin/2fa/disable', { password: disablePassword });
      setTwoFactorStatus({ enabled: false, verifiedAt: null });
      setShowSetup2FA(false);
      setQrCode('');
      setSecret('');
      setDisablePassword('');
      showNotification('success', '2FA désactivé');
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Erreur');
    } finally {
      setSetting2FA(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('success', 'Copié !');
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  // Issuing a repair code is the same trust as approving a shop, so it takes the same permission.
  const canRepairServers = admin?.role === 'owner' || admin?.role === 'super_admin' || hasPermission('approvals.manage');

  const tabs = [
    { id: 'general', label: 'Général', icon: faCog },
    { id: 'appearance', label: 'Apparence', icon: faPalette },
    { id: 'notifications', label: 'Notifications', icon: faBell },
    { id: 'security', label: 'Sécurité', icon: faShieldAlt },
    { id: 'system', label: 'Système', icon: faDatabase },
    { id: 'banner', label: 'Bannière', icon: faBullhorn },
    ...(canRepairServers ? [{ id: 'repair', label: 'Réparation serveur', icon: faServer }] : []),
    ...(canRepairServers ? [{ id: 'reset', label: 'Mot de passe Desktop', icon: faUserLock }] : []),
  ];

  const loadBannerGroups = useCallback(async () => {
    if (bannerGroups.length > 0) return;
    try {
      setBannerGroupsLoading(true);
      const response = await api.get('/admin/groups');
      setBannerGroups(response.data || []);
    } catch (error) {
      console.error('Error loading groups for banner:', error);
    } finally {
      setBannerGroupsLoading(false);
    }
  }, [bannerGroups.length]);

  const newEmptyBanner = () => ({
    id: Date.now().toString(),
    enabled: true,
    text: '',
    type: 'info',
    scrolling: true,
    speed: 'medium',
    target: 'all',
    bg_color: '',
    text_color: '',
    font_size: 13,
    schedule_enabled: false,
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
  });

  const addBanner = () => {
    const b = newEmptyBanner();
    setBanners(prev => [...prev, b]);
    setExpandedBannerId(b.id);
    setHasChanges(true);
    loadBannerGroups();
  };

  const updateBanner = (id, field, value) => {
    setBanners(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));
    setHasChanges(true);
  };

  const deleteBanner = (id) => {
    setBanners(prev => prev.filter(b => b.id !== id));
    if (expandedBannerId === id) setExpandedBannerId(null);
    setHasChanges(true);
  };

  const BANNER_TYPES = [
    { id: 'info', label: 'Info', bg: '#3b82f6', text: '#fff' },
    { id: 'success', label: 'Succès', bg: '#10b981', text: '#fff' },
    { id: 'warning', label: 'Attention', bg: '#f59e0b', text: '#fff' },
    { id: 'error', label: 'Erreur', bg: '#ef4444', text: '#fff' },
    { id: 'announcement', label: 'Annonce', bg: '#8b5cf6', text: '#fff' },
    { id: 'news', label: 'Nouveauté', bg: '#0d9488', text: '#fff' },
  ];

  const FONT_SIZES = [
    { label: 'XS', value: 11 },
    { label: 'S', value: 13 },
    { label: 'M', value: 14 },
    { label: 'L', value: 16 },
    { label: 'XL', value: 18 },
  ];

  const getBannerPreviewStyle = (b) => {
    const typePreset = BANNER_TYPES.find(t => t.id === b.type);
    return {
      background: b.bg_color || typePreset?.bg || '#3b82f6',
      color: b.text_color || typePreset?.text || '#fff',
      fontSize: `${b.font_size || 13}px`,
    };
  };

  const renderBannerCard = (b) => {
    const isExpanded = expandedBannerId === b.id;
    const targetLabel = b.target === 'all'
      ? 'Tous les groupes'
      : bannerGroups.find(g => `groupe:${g.id}` === b.target)?.nom || b.target.replace('groupe:', 'Groupe: ');

    return (
      <div key={b.id} className={`banner-card ${isExpanded ? 'banner-card--expanded' : ''}`}>
        {/* Card header */}
        <div className="banner-card-header">
          <label className="toggle-switch banner-card-toggle">
            <input
              type="checkbox"
              checked={!!b.enabled}
              onChange={e => updateBanner(b.id, 'enabled', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <div className="banner-card-meta">
            <span className="banner-card-target">{targetLabel}</span>
            <span className="banner-card-text">{b.text || <em>Aucun message</em>}</span>
          </div>
          <div className="banner-card-actions">
            {b.schedule_enabled && <span className="banner-card-badge"><FontAwesomeIcon icon={faClock} /> Planifiée</span>}
            <button className="banner-card-btn" onClick={() => setExpandedBannerId(isExpanded ? null : b.id)}>
              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
            </button>
            <button className="banner-card-btn banner-card-btn--delete" onClick={() => deleteBanner(b.id)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Card body (expanded editor) */}
        {isExpanded && (
          <div className="banner-card-body">

            {/* Message */}
            <div className="setting-item setting-item--column">
              <div className="setting-info"><label>Message</label></div>
              <textarea
                className="banner-textarea"
                rows={2}
                value={b.text}
                placeholder="Saisissez le message à afficher..."
                onChange={e => updateBanner(b.id, 'text', e.target.value)}
              />
            </div>

            {/* Type */}
            <div className="setting-item setting-item--column">
              <div className="setting-info"><label>Type</label></div>
              <div className="banner-type-selector">
                {BANNER_TYPES.map(t => (
                  <button key={t.id}
                    className={`banner-type-btn ${b.type === t.id ? 'active' : ''}`}
                    style={{ background: t.bg, color: t.text }}
                    onClick={() => updateBanner(b.id, 'type', t.id)}
                  >{t.label}</button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div className="setting-item setting-item--column">
              <div className="setting-info"><label>Couleurs personnalisées</label></div>
              <div className="banner-color-pickers">
                <div className="banner-color-item">
                  <label>Fond</label>
                  <div className="banner-color-input-wrap">
                    <input type="color" value={b.bg_color || '#3b82f6'} onChange={e => updateBanner(b.id, 'bg_color', e.target.value)} />
                    {b.bg_color && <span className="banner-color-value">{b.bg_color}</span>}
                  </div>
                </div>
                <div className="banner-color-item">
                  <label>Texte</label>
                  <div className="banner-color-input-wrap">
                    <input type="color" value={b.text_color || '#ffffff'} onChange={e => updateBanner(b.id, 'text_color', e.target.value)} />
                    {b.text_color && <span className="banner-color-value">{b.text_color}</span>}
                  </div>
                </div>
                <button className="btn-secondary banner-reset-colors"
                  onClick={() => { updateBanner(b.id, 'bg_color', ''); updateBanner(b.id, 'text_color', ''); }}>
                  <FontAwesomeIcon icon={faTimes} /> Réinitialiser
                </button>
              </div>
            </div>

            {/* Font size */}
            <div className="setting-item">
              <div className="setting-info"><label>Taille du texte</label></div>
              <div className="banner-font-size-selector">
                {FONT_SIZES.map(s => (
                  <button key={s.value}
                    className={`banner-size-btn ${b.font_size === s.value ? 'active' : ''}`}
                    onClick={() => updateBanner(b.id, 'font_size', s.value)}
                  >{s.label}</button>
                ))}
              </div>
            </div>

            {/* Scrolling */}
            <div className="setting-item">
              <div className="setting-info"><label>Défilement</label></div>
              <label className="toggle-switch">
                <input type="checkbox" checked={!!b.scrolling} onChange={e => updateBanner(b.id, 'scrolling', e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {/* Speed */}
            {b.scrolling && (
              <div className="setting-item">
                <div className="setting-info"><label>Vitesse</label></div>
                <div className="banner-speed-selector">
                  {[{ id: 'slow', label: 'Lent' }, { id: 'medium', label: 'Moyen' }, { id: 'fast', label: 'Rapide' }].map(s => (
                    <button key={s.id}
                      className={`banner-speed-btn ${b.speed === s.id ? 'active' : ''}`}
                      onClick={() => updateBanner(b.id, 'speed', s.id)}
                    >{s.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Target */}
            <div className="setting-item setting-item--column" onClick={loadBannerGroups}>
              <div className="setting-info"><label>Afficher pour</label></div>
              <div className="banner-target-selector">
                <label className={`banner-target-option ${b.target === 'all' ? 'active' : ''}`}>
                  <input type="radio" name={`banner_target_${b.id}`} checked={b.target === 'all'}
                    onChange={() => updateBanner(b.id, 'target', 'all')} />
                  Tous les groupes
                </label>
                <label className={`banner-target-option ${b.target !== 'all' ? 'active' : ''}`}>
                  <input type="radio" name={`banner_target_${b.id}`} checked={b.target !== 'all'}
                    onChange={() => {
                      updateBanner(b.id, 'target', bannerGroups.length > 0 ? `groupe:${bannerGroups[0].id}` : 'groupe:');
                      loadBannerGroups();
                    }} />
                  Espace spécifique
                </label>
              </div>
              {b.target !== 'all' && (
                <div className="banner-group-select-wrap">
                  {bannerGroupsLoading
                    ? <span className="banner-groups-loading"><FontAwesomeIcon icon={faSpinner} spin /> Chargement...</span>
                    : (
                      <select className="banner-group-select"
                        value={b.target.replace('groupe:', '')}
                        onChange={e => updateBanner(b.id, 'target', `groupe:${e.target.value}`)}
                      >
                        <option value="">-- Sélectionner un espace --</option>
                        {bannerGroups.map(g => <option key={g.id} value={g.id}>{g.nom}</option>)}
                      </select>
                    )
                  }
                </div>
              )}
            </div>

            {/* Schedule */}
            <div className="setting-item setting-item--column">
              <div className="setting-info"><label>Planification</label></div>
              <label className="toggle-switch">
                <input type="checkbox" checked={!!b.schedule_enabled}
                  onChange={e => updateBanner(b.id, 'schedule_enabled', e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
              {b.schedule_enabled && (
                <div className="banner-schedule-grid">
                  <div className="banner-schedule-row">
                    <span className="banner-schedule-label">Début</span>
                    <input type="date" className="banner-schedule-input" value={b.start_date}
                      onChange={e => updateBanner(b.id, 'start_date', e.target.value)} />
                    <input type="time" className="banner-schedule-input" value={b.start_time}
                      onChange={e => updateBanner(b.id, 'start_time', e.target.value)} />
                  </div>
                  <div className="banner-schedule-row">
                    <span className="banner-schedule-label">Fin</span>
                    <input type="date" className="banner-schedule-input" value={b.end_date}
                      onChange={e => updateBanner(b.id, 'end_date', e.target.value)} />
                    <input type="time" className="banner-schedule-input" value={b.end_time}
                      onChange={e => updateBanner(b.id, 'end_time', e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="setting-item setting-item--column">
              <div className="setting-info"><label>Aperçu</label></div>
              <div className="banner-preview" style={getBannerPreviewStyle(b)}>
                <span className="banner-preview__text">{b.text || 'Votre message ici...'}</span>
              </div>
            </div>

          </div>
        )}
      </div>
    );
  };

  const renderBannerSettings = () => (
    <div className="settings-section">
      <h3><FontAwesomeIcon icon={faBullhorn} /> Bannières d'information</h3>

      <div className="banners-list">
        {banners.length === 0 && (
          <div className="banners-empty">Aucune bannière configurée. Cliquez sur "Nouvelle bannière" pour commencer.</div>
        )}
        {banners.map(b => renderBannerCard(b))}
      </div>

      <button className="btn-primary banner-add-btn" onClick={addBanner}>
        <FontAwesomeIcon icon={faPlus} /> Nouvelle bannière
      </button>
    </div>
  );

  const renderGeneralSettings = () => (
    <div className="settings-section">
      <h3><FontAwesomeIcon icon={faGlobe} /> Informations Générales</h3>
      
      <div className="setting-item">
        <div className="setting-info">
          <label>Nom du site</label>
          <p>Le nom affiché dans le dashboard</p>
        </div>
        <input
          type="text"
          value={settings.site_name}
          onChange={(e) => handleChange('site_name', e.target.value)}
        />
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>URL du site</label>
          <p>L'adresse principale de votre site</p>
        </div>
        <input
          type="url"
          value={settings.site_url}
          onChange={(e) => handleChange('site_url', e.target.value)}
        />
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Mode maintenance</label>
          <p>Activer le mode maintenance pour bloquer l'accès</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.maintenance_mode}
            onChange={(e) => handleChange('maintenance_mode', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="settings-section">
      <h3><FontAwesomeIcon icon={faPalette} /> Personnalisation</h3>
      
      <div className="setting-item">
        <div className="setting-info">
          <label>Thème</label>
          <p>Choisir entre le mode clair ou sombre</p>
        </div>
        <div className="theme-buttons">
          <button
            className={`theme-btn ${settings.default_theme === 'light' ? 'active' : ''}`}
            onClick={() => handleChange('default_theme', 'light')}
          >
            <FontAwesomeIcon icon={faSun} /> Clair
          </button>
          <button
            className={`theme-btn ${settings.default_theme === 'dark' ? 'active' : ''}`}
            onClick={() => handleChange('default_theme', 'dark')}
          >
            <FontAwesomeIcon icon={faMoon} /> Sombre
          </button>
        </div>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Couleur principale</label>
          <p>La couleur de marque utilisée dans l'interface</p>
        </div>
        <div className="color-options">
          {['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'].map(color => (
            <button
              key={color}
              className={`color-btn ${settings.primary_color === color ? 'active' : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => handleChange('primary_color', color)}
            >
              {settings.primary_color === color && <FontAwesomeIcon icon={faCheck} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3><FontAwesomeIcon icon={faEnvelope} /> Préférences de Notifications</h3>
      
      <div className="setting-item">
        <div className="setting-info">
          <label>Notifications par email</label>
          <p>Recevoir des notifications importantes par email</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.email_notifications}
            onChange={(e) => handleChange('email_notifications', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Alertes de connexion</label>
          <p>Notification lors de nouvelles connexions</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.login_alerts}
            onChange={(e) => handleChange('login_alerts', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Résumé d'activité</label>
          <p>Fréquence du rapport d'activité</p>
        </div>
        <select
          value={settings.activity_digest}
          onChange={(e) => handleChange('activity_digest', e.target.value)}
        >
          <option value="never">Jamais</option>
          <option value="daily">Quotidien</option>
          <option value="weekly">Hebdomadaire</option>
          <option value="monthly">Mensuel</option>
        </select>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Nouvel utilisateur</label>
          <p>Notification lors de l'inscription d'un utilisateur</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.notify_on_new_user}
            onChange={(e) => handleChange('notify_on_new_user', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Nouveau groupe</label>
          <p>Notification lors de la création d'un groupe</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.notify_on_new_group}
            onChange={(e) => handleChange('notify_on_new_group', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3><FontAwesomeIcon icon={faLock} /> Paramètres de Sécurité</h3>
      
      {/* Personal 2FA Setup */}
      <div className="setting-item two-factor-section">
        <div className="setting-info">
          <label><FontAwesomeIcon icon={faQrcode} /> Authentification à deux facteurs (2FA)</label>
          <p>Sécurisez votre compte avec une vérification en deux étapes</p>
        </div>
        <div className="two-factor-actions">
          {twoFactorStatus.enabled ? (
            <div className="two-factor-enabled">
              <span className="status-badge success">
                <FontAwesomeIcon icon={faCheck} /> Activé
              </span>
              <button 
                className="btn-danger-outline"
                onClick={() => setShowSetup2FA(true)}
              >
                Désactiver
              </button>
            </div>
          ) : (
            <button 
              className="btn-primary"
              onClick={handleSetup2FA}
              disabled={setting2FA}
            >
              {setting2FA ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faKey} />}
              {' '}Configurer 2FA
            </button>
          )}
        </div>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Expiration de session (minutes)</label>
          <p>Durée d'inactivité avant déconnexion automatique</p>
        </div>
        <select
          value={settings.session_timeout}
          onChange={(e) => handleChange('session_timeout', parseInt(e.target.value))}
        >
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes</option>
          <option value={60}>1 heure</option>
          <option value={120}>2 heures</option>
          <option value={480}>8 heures</option>
        </select>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Mots de passe forts</label>
          <p>Exiger des mots de passe complexes</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.require_strong_passwords}
            onChange={(e) => handleChange('require_strong_passwords', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>2FA obligatoire</label>
          <p>Exiger la 2FA pour tous les admins</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.two_factor_required}
            onChange={(e) => handleChange('two_factor_required', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Limite tentatives connexion</label>
          <p>Nombre d'échecs avant blocage</p>
        </div>
        <select
          value={settings.login_attempt_limit}
          onChange={(e) => handleChange('login_attempt_limit', parseInt(e.target.value))}
        >
          <option value={3}>3 tentatives</option>
          <option value={5}>5 tentatives</option>
          <option value={10}>10 tentatives</option>
        </select>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Durée de blocage (minutes)</label>
          <p>Temps de blocage après trop de tentatives</p>
        </div>
        <select
          value={settings.lockout_duration}
          onChange={(e) => handleChange('lockout_duration', parseInt(e.target.value))}
        >
          <option value={5}>5 minutes</option>
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes</option>
          <option value={60}>1 heure</option>
        </select>
      </div>
    </div>
  );

  const renderSystemSettings = () => (
    <div className="settings-section">
      <h3><FontAwesomeIcon icon={faServer} /> Configuration Système</h3>
      
      <div className="setting-item">
        <div className="setting-info">
          <label>Sauvegarde automatique</label>
          <p>Activer les sauvegardes automatiques</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.auto_backup}
            onChange={(e) => handleChange('auto_backup', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Fréquence de sauvegarde</label>
          <p>Intervalle entre les sauvegardes</p>
        </div>
        <select
          value={settings.backup_frequency}
          onChange={(e) => handleChange('backup_frequency', e.target.value)}
          disabled={!settings.auto_backup}
        >
          <option value="hourly">Toutes les heures</option>
          <option value="daily">Quotidienne</option>
          <option value="weekly">Hebdomadaire</option>
        </select>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Rétention des logs (jours)</label>
          <p>Durée de conservation des journaux d'activité</p>
        </div>
        <select
          value={settings.log_retention}
          onChange={(e) => handleChange('log_retention', parseInt(e.target.value))}
        >
          <option value={30}>30 jours</option>
          <option value={60}>60 jours</option>
          <option value={90}>90 jours</option>
          <option value={180}>180 jours</option>
          <option value={365}>1 an</option>
        </select>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Mode debug</label>
          <p>Activer les logs détaillés pour le débogage</p>
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={settings.debug_mode}
            onChange={(e) => handleChange('debug_mode', e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>

      {/* Log Viewer Button - only show when debug mode is enabled */}
      {settings.debug_mode && (
        <div className="setting-item log-viewer-trigger">
          <div className="setting-info">
            <label><FontAwesomeIcon icon={faTerminal} /> Visualiseur de logs</label>
            <p>Consulter les logs système détaillés</p>
          </div>
          <button 
            className="btn-view-logs"
            onClick={() => setShowLogViewer(true)}
          >
            <FontAwesomeIcon icon={faEye} /> Voir les logs
          </button>
        </div>
      )}

      {/* System Info */}
      <div className="system-info">
        <h4><FontAwesomeIcon icon={faInfoCircle} /> Informations Système</h4>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Version</span>
            <span className="info-value">1.0.0</span>
          </div>
          <div className="info-item">
            <span className="info-label">Admin connecté</span>
            <span className="info-value">{admin?.email}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Rôle</span>
            <span className="info-value">
              {admin?.role === 'owner' ? 'Propriétaire' : admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Dernière connexion</span>
            <span className="info-value">
              {admin?.last_login ? new Date(admin.last_login).toLocaleString('fr-FR') : 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'appearance':
        return renderAppearanceSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      case 'system':
        return renderSystemSettings();
      case 'banner':
        return renderBannerSettings();
      case 'repair':
        return canRepairServers ? <RepairCodePanel /> : renderGeneralSettings();
      case 'reset':
        return canRepairServers ? <ResetCodePanel /> : renderGeneralSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="settings-page">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon 
            icon={notification.type === 'success' ? faCheck : faExclamationTriangle} 
          />
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div className="header-title">
          <h1><FontAwesomeIcon icon={faCog} /> Paramètres</h1>
          <p>Configuration et préférences du dashboard</p>
        </div>
        <div className="header-actions">
          {hasChanges && (
            <>
              <button className="btn-secondary" onClick={handleReset}>
                <FontAwesomeIcon icon={faUndo} /> Annuler
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                <FontAwesomeIcon icon={faSave} /> 
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="settings-layout">
        {/* Sidebar Tabs */}
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <FontAwesomeIcon icon={tab.icon} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="settings-content">
          {loading ? (
            <div className="loading-state">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Chargement des paramètres...</p>
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {showSetup2FA && (
        <div className="modal-overlay" onClick={() => !backupCodes.length && setShowSetup2FA(false)}>
          <div className="modal-content modal-2fa" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faQrcode} />
                {twoFactorStatus.enabled ? ' Désactiver 2FA' : ' Configuration 2FA'}
              </h2>
              <button className="close-btn" onClick={() => {
                setShowSetup2FA(false);
                setQrCode('');
                setSecret('');
                setVerifyCode('');
                setBackupCodes([]);
                setDisablePassword('');
              }}>×</button>
            </div>
            <div className="modal-body">
              {twoFactorStatus.enabled ? (
                // Disable 2FA form
                <div className="disable-2fa-form">
                  <p className="warning-text">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    La désactivation de 2FA réduira la sécurité de votre compte.
                  </p>
                  <div className="form-group">
                    <label>Mot de passe actuel</label>
                    <input
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      placeholder="Entrez votre mot de passe"
                    />
                  </div>
                  <button 
                    className="btn-danger"
                    onClick={handleDisable2FA}
                    disabled={setting2FA || !disablePassword}
                  >
                    {setting2FA ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Désactiver 2FA'}
                  </button>
                </div>
              ) : backupCodes.length > 0 ? (
                // Show backup codes
                <div className="backup-codes-section">
                  <div className="success-message">
                    <FontAwesomeIcon icon={faCheck} />
                    <h3>2FA activé avec succès !</h3>
                  </div>
                  <p>Sauvegardez ces codes de récupération. Ils vous permettront d'accéder à votre compte si vous perdez votre téléphone.</p>
                  <div className="backup-codes-grid">
                    {backupCodes.map((code, i) => (
                      <div key={i} className="backup-code">{code}</div>
                    ))}
                  </div>
                  <button 
                    className="btn-secondary"
                    onClick={() => copyToClipboard(backupCodes.join('\n'))}
                  >
                    <FontAwesomeIcon icon={faCopy} /> Copier les codes
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      setShowSetup2FA(false);
                      setBackupCodes([]);
                    }}
                  >
                    Terminé
                  </button>
                </div>
              ) : (
                // Setup 2FA form
                <div className="setup-2fa-form">
                  {qrCode ? (
                    <>
                      <p>Scannez ce QR code avec votre application d'authentification (Google Authenticator, Authy, etc.)</p>
                      <div className="qr-code-container">
                        <img src={qrCode} alt="QR Code 2FA" />
                      </div>
                      <div className="secret-key">
                        <label>Ou entrez cette clé manuellement :</label>
                        <div className="secret-display">
                          <code>{secret}</code>
                          <button onClick={() => copyToClipboard(secret)}>
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Code de vérification</label>
                        <input
                          type="text"
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Entrez le code à 6 chiffres"
                          maxLength={6}
                          className="verify-code-input"
                        />
                      </div>
                      <button 
                        className="btn-primary"
                        onClick={handleVerify2FA}
                        disabled={setting2FA || verifyCode.length !== 6}
                      >
                        {setting2FA ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Vérifier et activer'}
                      </button>
                    </>
                  ) : (
                    <div className="loading-state">
                      <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                      <p>Génération du QR code...</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Log Viewer Modal */}
      {showLogViewer && (
        <div className="modal-overlay" onClick={() => setShowLogViewer(false)}>
          <div className="modal-content log-viewer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faTerminal} /> Logs Système</h2>
              <button className="close-btn" onClick={() => setShowLogViewer(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Log Stats */}
              <div className="log-stats">
                <div className="log-stat debug">
                  <FontAwesomeIcon icon={faBug} />
                  <span className="count">{logCounts.debug}</span>
                  <span className="label">Debug</span>
                </div>
                <div className="log-stat info">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span className="count">{logCounts.info}</span>
                  <span className="label">Info</span>
                </div>
                <div className="log-stat warn">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span className="count">{logCounts.warn}</span>
                  <span className="label">Warn</span>
                </div>
                <div className="log-stat error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span className="count">{logCounts.error}</span>
                  <span className="label">Error</span>
                </div>
              </div>

              {/* Filters */}
              <div className="log-filters">
                <div className="filter-group">
                  <FontAwesomeIcon icon={faFilter} />
                  <select 
                    value={logFilter.level} 
                    onChange={(e) => setLogFilter(prev => ({ ...prev, level: e.target.value }))}
                  >
                    <option value="">Tous les niveaux</option>
                    <option value="debug">Debug</option>
                    <option value="info">Info</option>
                    <option value="warn">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                <div className="filter-group">
                  <select 
                    value={logFilter.source} 
                    onChange={(e) => setLogFilter(prev => ({ ...prev, source: e.target.value }))}
                  >
                    <option value="">Toutes les sources</option>
                    {logSources.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group search-group">
                  <FontAwesomeIcon icon={faSearch} />
                  <input 
                    type="text"
                    placeholder="Rechercher..."
                    value={logFilter.search}
                    onChange={(e) => setLogFilter(prev => ({ ...prev, search: e.target.value }))}
                  />
                </div>
                <button 
                  className={`btn-refresh ${loadingLogs ? 'loading' : ''}`}
                  onClick={loadSystemLogs}
                  disabled={loadingLogs}
                  title="Actualiser les logs"
                >
                  <FontAwesomeIcon icon={faSync} />
                </button>
                {(admin?.role === 'owner' || admin?.role === 'super_admin') && (
                  <button 
                    className="btn-clear-logs"
                    onClick={() => handleClearLogs()}
                    disabled={clearingLogs}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    {clearingLogs ? 'Suppression...' : 'Vider'}
                  </button>
                )}
              </div>

              {/* Logs Table */}
              <div className="log-table-container">
                {loadingLogs ? (
                  <div className="loading-state">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <p>Chargement des logs...</p>
                  </div>
                ) : systemLogs.length === 0 ? (
                  <div className="empty-logs">
                    <FontAwesomeIcon icon={faTerminal} size="3x" />
                    <p>Aucun log système trouvé</p>
                    <span>Les logs apparaîtront ici lorsque le mode debug est activé</span>
                  </div>
                ) : (
                  <table className="log-table">
                    <thead>
                      <tr>
                        <th>Horodatage</th>
                        <th>Niveau</th>
                        <th>Source</th>
                        <th>Message</th>
                        <th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemLogs.map(log => (
                        <tr key={log.id} className={`log-row level-${log.level}`}>
                          <td className="log-time">
                            {new Date(log.created_at).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })}
                          </td>
                          <td>
                            <span className={`log-level ${log.level}`}>
                              {log.level.toUpperCase()}
                            </span>
                          </td>
                          <td className="log-source">{log.source || '-'}</td>
                          <td className="log-message">
                            {log.message}
                            {log.details && (
                              <details className="log-details">
                                <summary>Détails</summary>
                                <pre>{JSON.stringify(log.details, null, 2)}</pre>
                              </details>
                            )}
                          </td>
                          <td className="log-ip">{log.ip_address || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
